// src/utils/consistencyScore.js
import { totalsByDate } from "./dailyTotals";

/**
 * Compute the 0–100 gamified consistency score over the last N days.
 *
 * Equal 25% weight for each of:
 *  1. % days with weight logged
 *  2. % days with food logged
 *  3. % days hitting calorie target (within 110%)
 *  4. % days hitting protein target (≥ 100%)
 *
 * @param {object} params
 * @param {Array}  params.weights
 * @param {Array}  params.foods
 * @param {object} params.profile
 * @param {number} params.days - default 30
 * @returns {number} 0–100
 */
export function computeConsistencyScore({ weights = [], foods = [], profile, days = 30 }) {
  if (!profile) return 0;

  const today = new Date();
  const dates = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    return d.toISOString().slice(0, 10);
  });

  const weightSet   = new Set(weights.map((e) => e.date));
  const foodDateMap = totalsByDate(foods);

  const calTarget     = profile.calorie_target || 99999;
  const proteinTarget = profile.protein_target || 999;

  let weightLogged = 0, foodLogged = 0, calHit = 0, proteinHit = 0;

  for (const d of dates) {
    if (weightSet.has(d))                         weightLogged++;
    const f = foodDateMap[d];
    if (f && f.calories > 0) {
      foodLogged++;
      if (f.calories <= calTarget * 1.10)         calHit++;
      if (f.protein  >= proteinTarget)            proteinHit++;
    }
  }

  const score =
    (weightLogged / days) * 25 +
    (foodLogged   / days) * 25 +
    (calHit       / days) * 25 +
    (proteinHit   / days) * 25;

  return Math.round(score);
}

/**
 * Return color class based on score.
 */
export function scoreColor(score) {
  if (score >= 80) return "text-emerald-400";
  if (score >= 50) return "text-yellow-400";
  return "text-red-400";
}

export function scoreBgColor(score) {
  if (score >= 80) return "bg-emerald-500";
  if (score >= 50) return "bg-yellow-500";
  return "bg-red-500";
}

export function scoreLabel(score) {
  if (score >= 80) return "Excellent";
  if (score >= 65) return "Good";
  if (score >= 50) return "Fair";
  if (score >= 30) return "Needs Work";
  return "Getting Started";
}
